import { Link } from "react-router-dom";
import PageLayout from "@/components/layout/PageLayout";
import PageHero from "@/components/shared/PageHero";
import { usePageContent } from "@/hooks/usePageContent";

const engines = [
  {
    icon: "ri-cpu-line",
    title: "HBU Engine (Highest & Best Use)",
    badge: "Core Algorithm",
    desc: "The HBU Engine is the backbone of every CashMyProperty report. It ingests land GPS coordinates, size, zoning rules, FSI limits, demographic data, and infrastructure proximity to score your land across six use categories.",
    points: ["Residential (apartments, villas, plotted development)", "Commercial (offices, co-working, retail malls)", "Logistical (warehouses, cold storage, data centers)", "Hospitality (hotels, serviced apartments, resorts)", "Retail / QSR (drive-thrus, fuel stations, supermarkets)", "Agri-commercial (solar, agritourism, organic farming)"],
    color: "primary",
  },
  {
    icon: "ri-line-chart-line",
    title: "Valuation Predictor",
    badge: "Predictive AI",
    desc: "Our ML model forecasts land value appreciation over 3, 5, and 10-year horizons. It pulls from 40+ live data feeds including metro line announcements, highway tenders, airport expansion plans, and smart city notifications.",
    points: ["Real-time infrastructure pipeline tracking", "RERA project density mapping", "Historical appreciation regression models", "3/5/10-year ROI projections", "Comparable transaction benchmarking"],
    color: "secondary",
  },
  {
    icon: "ri-store-2-line",
    title: "Brand Match Algorithm",
    badge: "Live Network",
    desc: "We maintain a live database of 200+ national and international brands with active site acquisition requirements. The Brand Match engine cross-references your land's profile against their criteria — yielding the highest-probability brand matches.",
    points: ["QSR chains: Domino's, McDonald's, Starbucks", "Fuel & mobility: Shell, BPCL, COCO, Charge+Zone", "E-commerce: Amazon, Flipkart, Delhivery", "Retail: DMart, Spencer's, Reliance Smart", "Updated monthly with new brand requirements"],
    color: "accent",
  },
];

const metrics = [
  { value: "40+", label: "Live data feeds" },
  { value: "200+", label: "Brand integrations" },
  { value: "94%", label: "Prediction accuracy" },
  { value: "48 Hrs", label: "Report generation" },
];

export default function AITechnologyPage() {
  const { getContent } = usePageContent('ai-technology');
  const ctaHeading = getContent('ai-cta-heading') || 'See the AI Engine Work on Your Land';
  const ctaSub = getContent('ai-cta-sub') || 'Submit your land details and get a full AI Feasibility Report delivered in 48 hours — reviewed by our real estate experts.';

  return (
    <PageLayout>
      <PageHero
        badge="AI Technology"
        title="The AI Brain"
        highlight="Behind Every Deal"
        subtitle="We don't guess. We analyze millions of data points to determine exactly what your land is worth."
        image="https://readdy.ai/api/search-image?query=Abstract%20futuristic%20AI%20neural%20network%20visualization%20with%20glowing%20green%20nodes%20and%20golden%20connection%20lines%2C%20dark%20deep%20background%2C%20holographic%20data%20streams%2C%20property%20map%20overlays%2C%20cinematic%20technology%20concept%20art%2C%20teal%20and%20amber%20accent%20lights%2C%20highly%20detailed&width=1800&height=800&seq=ai-brain-hero-page&orientation=landscape"
      />

      {/* Metrics */}
      <section className="py-14 bg-background-100 border-b border-primary-900/20">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {metrics.map((m) => (
              <div key={m.label} className="text-center">
                <div className="text-3xl md:text-4xl font-display font-bold text-foreground-950">{m.value}</div>
                <div className="text-sm text-foreground-500 mt-1">{m.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Engines */}
      <section className="py-24 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">
              Three AI Engines. One Powerful Report.
            </h2>
            <p className="text-foreground-600">
              Each engine is purpose-built for a different dimension of land analysis. Together, they produce an
              unmatched feasibility output.
            </p>
          </div>

          <div className="space-y-10">
            {engines.map((e) => (
              <div key={e.title} className="p-8 md:p-10 rounded-2xl card-dark">
                <div className="grid md:grid-cols-[280px_1fr] gap-8">
                  <div>
                    <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-background-50 border border-primary-500/30 mb-5">
                      <i className={`${e.icon} text-3xl text-primary-500`} />
                    </div>
                    <span className="inline-block text-xs font-medium px-3 py-1 rounded-full bg-primary-500/10 text-primary-700 mb-4">
                      {e.badge}
                    </span>
                    <h3 className="text-xl md:text-2xl font-display font-bold text-foreground-950">{e.title}</h3>
                  </div>
                  <div>
                    <p className="text-foreground-600 leading-relaxed mb-6">{e.desc}</p>
                    <div className="grid sm:grid-cols-2 gap-2">
                      {e.points.map((p) => (
                        <div key={p} className="flex items-start gap-2 text-sm text-foreground-600">
                          <i className="ri-check-line text-primary-500 mt-0.5 shrink-0" />
                          {p}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process visual */}
      <section className="py-20 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-xl mx-auto mb-12">
            <h2 className="text-2xl md:text-3xl font-display font-bold text-foreground-950 mb-4">
              From Land Details to AI Report in 48 Hours
            </h2>
          </div>
          <div className="grid md:grid-cols-5 gap-4 items-center">
            {[
              { icon: "ri-map-pin-2-line", label: "GPS & Details" },
              { icon: "ri-database-2-line", label: "Data Ingestion" },
              { icon: "ri-cpu-line", label: "AI Processing" },
              { icon: "ri-file-chart-line", label: "Report Draft" },
              { icon: "ri-check-double-line", label: "Expert Review" },
            ].map((step, i) => (
              <div key={step.label} className="flex flex-col items-center text-center gap-3 relative">
                {i < 4 && <div className="hidden md:block absolute top-6 left-[60%] w-full h-px bg-primary-900/30" />}
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/20 border border-primary-700/30 z-10">
                  <i className={`${step.icon} text-xl text-primary-500`} />
                </div>
                <span className="text-sm text-foreground-600">{step.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8 text-center max-w-2xl">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">
            {ctaHeading}
          </h2>
          <p className="text-foreground-600 mb-8">
            {ctaSub}
          </p>
          <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap">
            Request My AI Report — ₹4,999
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </section>
    </PageLayout>
  );
}